// Article "Première leçon moto : bien débuter sur le plateau"
// Sujet éditorial pédagogique + présentation naturelle du Livret de progression CODE 2 ROUES.
import { Eye, Target, Route, Bike, ShieldCheck, BookOpen, Sparkles, Feather } from 'lucide-react';

export const premiereMotoArticle = {
  slug: 'premiere-lecon-moto-bien-debuter-plateau',
  weekVolume: 34,
  weekYear: 2026,
  monthLabel: 'Août 2026',
  category: 'Pédagogie moto · Plateau',
  shortTitle: 'Bien débuter sur le plateau',
  title: 'Première leçon moto : bien débuter sur le plateau',
  kicker: 'Formation initiale · Plateau A1/A2',
  subtitle: "Avant de chercher la vitesse, il faut comprendre sa moto. Une première leçon réussie ne consiste pas à réaliser le parcours d'examen : elle consiste à prendre confiance et à construire les bons automatismes.",
  heroImage: 'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: "Motard sur une moto naked en formation plateau — première leçon",
  topRightKicker: { line1: 'Dossier CODE 2 ROUES', line2: 'Première leçon · Plateau' },
  readTimeMin: 7,
  seoDescription: "Ta première leçon moto sur le plateau : comprendre les commandes, le regard, la position, le point de patinage, le freinage, le slalom et l'évitement. Guide CODE 2 ROUES + Livret de progression A1/A2 téléchargeable.",
  seoKeywords: 'première leçon moto plateau, débuter moto A2, point de patinage, regard moto, position moto débutant, livret progression plateau A1 A2',
  about: "Formation moto initiale, plateau, premières heures, apprentissage progressif",
  source: { label: 'CODE 2 ROUES · Rédaction', refs: 'Article rédigé par Bruno · Équipe Code 2 Roues · Approche pédagogique CODE 2 ROUES.' },
  chapo: {
    kicker: 'Pourquoi cet article',
    body: "La première leçon moto est un moment particulier : casque, embrayage, freins, sélecteur, équilibre… et son propre stress. Une bonne première séance ne cherche pas à aller vite. Elle cherche à <strong>comprendre la moto, prendre confiance et construire les bons automatismes</strong>.",
    sideStat: {
      kicker: 'À retenir',
      big: '3',
      body: "Trois piliers : <strong>regard</strong>, <strong>position</strong>, <strong>point de patinage</strong>. Tout le reste de la formation en découle.",
      accent: '#f97316',
    },
  },
  pullQuote: {
    text: "Le plateau permet d'apprendre à connaître les réactions de la moto dans un environnement sécurisé. Toutes ces compétences seront ensuite utilisées sur la route.",
    source: 'CODE 2 ROUES — Le plateau n\'est pas seulement un examen',
  },
  sections: [
    {
      part: 'Partie 1 — La moto',
      title: 'Faire connaissance avec la moto',
      paragraphs: [
        "Avant même de démarrer le moteur, prends quelques minutes pour découvrir ta machine. Tu dois progressivement identifier <strong>quatre commandes essentielles</strong> et savoir les utiliser sans y penser.",
        "Au début, garde surtout les <strong>mains souples</strong> sur les poignées. Une crispation excessive sur le guidon rend la conduite moins précise et fatigue rapidement.",
      ],
      cards: [
        { icon: Bike, accent: '#f97316', kicker: 'Main gauche', title: 'Embrayage', body: 'La commande la plus fine — au cœur du contrôle basse vitesse.' },
        { icon: Bike, accent: '#eab308', kicker: 'Main droite', title: 'Accélérateur + frein avant', body: '≈ 70 % du freinage vient de ce levier. Progressif, jamais brutal.' },
        { icon: Bike, accent: '#22c55e', kicker: 'Pied gauche', title: 'Sélecteur de vitesses', body: 'Haut/bas — coordonné avec l\'embrayage.' },
        { icon: Bike, accent: '#06b6d4', kicker: 'Pied droit', title: 'Frein arrière', body: 'Régule et stabilise, notamment à basse allure.' },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 2 — Le regard',
      title: 'Probablement ton meilleur outil',
      paragraphs: [
        "C'est l'un des principes les plus importants à comprendre dès les premières heures : <strong>la moto va généralement là où tu regardes</strong>.",
        "Si tu fixes le cône que tu veux éviter, tu risques de te rapprocher de lui. Si tu regardes ta roue avant, tu perds les informations dont tu as besoin pour préparer la suite. Ton regard doit progressivement devenir : <strong>lointain, mobile et orienté vers la sortie</strong> de la trajectoire.",
      ],
      highlight: {
        kicker: '👀 La règle d\'or',
        title: 'Les cônes ne sont pas des aimants pour tes yeux',
        body: "Sur le plateau, apprends à regarder <strong>vers la sortie de chaque exercice</strong>, jamais les obstacles. Le corps suit le regard.",
        accent: '#0ea5e9',
      },
    },
    {
      part: 'Partie 3 — La position',
      title: 'Trouver une position naturelle',
      paragraphs: [
        "Un élève débutant a souvent tendance à vouloir <strong>contrôler la moto avec les bras</strong>. Résultat : épaules relevées, guidon serré, mouvements brusques et fatigue rapide.",
        "Une meilleure position repose sur plusieurs points d'appui : les <strong>pieds</strong> correctement placés sur les repose-pieds, les <strong>genoux</strong> au contact de la moto, le <strong>bassin</strong> stable, les <strong>mains souples</strong> sur le guidon, et surtout le <strong>regard</strong> qui anticipe.",
        "La moto ne se maîtrise pas par la force. Elle se maîtrise par <strong>l'équilibre, la précision et la coordination</strong>.",
      ],
    },
    {
      part: 'Partie 4 — La technique clé',
      title: 'Le point de patinage : la clé du lent',
      paragraphs: [
        "Lors des premières séances, l'un des apprentissages majeurs consiste à découvrir <strong>le point de patinage de l'embrayage</strong>. Il s'agit de la zone où l'embrayage commence progressivement à transmettre la puissance du moteur à la roue arrière.",
        "Au lieu d'utiliser l'embrayage comme un simple interrupteur « ouvert / fermé », l'élève apprend à <strong>travailler dans cette zone intermédiaire</strong>. C'est fondamental pour maîtriser la moto à basse vitesse.",
      ],
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: 'Guide', title: 'Le regard', body: 'Il définit la trajectoire à venir.' },
        { icon: Feather, accent: '#22c55e', kicker: 'Motricité', title: 'L\'embrayage', body: 'Il régule la puissance transmise à la roue.' },
        { icon: ShieldCheck, accent: '#eab308', kicker: 'Stabilité', title: 'Le frein arrière', body: 'Il régule et stabilise l\'ensemble.' },
      ],
      cardsCols: 3,
    },
    {
      part: 'Partie 5 — Freiner',
      title: 'Puissance oui, brutalité non',
      paragraphs: [
        "Le freinage d'urgence impressionne souvent les élèves lorsqu'ils découvrent le plateau. Pourtant, l'objectif n'est pas de <em>« planter les freins »</em>.",
        "Il faut apprendre à construire un <strong>freinage efficace, progressif et maîtrisé</strong>, tout en conservant une moto stable. On commence par comprendre le fonctionnement des commandes avant d'augmenter l'allure.",
      ],
      highlight: {
        kicker: '🎯 La règle d\'or',
        title: 'Technique avant vitesse',
        body: "C'est une règle qui accompagne toute la formation. Un freinage à 30 km/h propre vaut mieux qu'un freinage à 50 km/h chaotique.",
        accent: '#22c55e',
      },
    },
    {
      part: 'Partie 6 — Slalom & évitement',
      title: 'Le regard change tout',
      paragraphs: [
        "Lorsque l'allure augmente, <strong>le regard devient encore plus important</strong>. Pendant le slalom, fixer chaque cône complique énormément l'exercice. L'objectif est au contraire d'apprendre à <strong>anticiper la porte suivante</strong> et regarder vers la sortie.",
        "Pour l'évitement, le principe change encore : il faut être capable de <strong>provoquer rapidement un changement de direction</strong> tout en conservant la maîtrise de la moto.",
        "Ces exercices permettent de développer des compétences qui dépassent largement l'examen. Ils préparent surtout le futur motard à <strong>réagir correctement sur la route</strong>.",
      ],
    },
    {
      part: 'Partie 7 — Approche CODE 2 ROUES',
      title: 'Et si tu dessinais toi-même ton parcours ?',
      paragraphs: [
        "Chez CODE 2 ROUES, nous avons volontairement choisi une <strong>approche différente</strong> dans notre livret de progression. Nous aurions pu simplement fournir un schéma du parcours à mémoriser. Mais il est beaucoup plus intéressant de <strong>demander à l'élève de le reconstruire lui-même</strong>.",
        "Sur une page dédiée du livret, tu peux donc <strong>dessiner ton parcours</strong> : place les cônes, indique le départ et l'arrivée, ajoute le sens de circulation et retrouve les différentes phases.",
        "Parce qu'être capable de <strong>dessiner et d'expliquer le parcours</strong>, c'est déjà commencer à le maîtriser mentalement. Et lorsque le cerveau sait où aller, le pilotage devient plus facile.",
      ],
      cards: [
        { icon: Route, accent: '#22c55e', kicker: 'Actif', title: 'Dessiner', body: 'L\'élève reconstruit le parcours de mémoire — c\'est déjà le maîtriser.' },
        { icon: Target, accent: '#f97316', kicker: 'Ancrage', title: 'Expliquer', body: 'Verbaliser chaque phase renforce durablement l\'apprentissage.' },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 8 — L\'outil',
      title: 'Un livret pour suivre toute ta progression',
      paragraphs: [
        "Le <strong>Livret de progression Plateau A1/A2 CODE 2 ROUES</strong> a été conçu pour accompagner l'élève pendant sa formation. Il ne remplace évidemment pas ton enseignant. Il sert à <strong>visualiser, comprendre et suivre</strong> ta progression.",
        "Tu y retrouveras notamment : la prise en main de la moto, les commandes, la position et le regard, le point de patinage, l'allure lente, le freinage d'urgence, la conduite avec passager, le slalom, l'évitement, ainsi qu'une grille de progression jusqu'à la préparation de l'examen.",
      ],
      highlight: {
        kicker: '📕 3 niveaux de progression',
        title: 'DÉCOUVERT → EN COURS → ACQUIS',
        body: "L'objectif n'est pas de réussir une manœuvre une seule fois. L'objectif est de <strong>devenir capable de la reproduire régulièrement</strong>.",
        accent: '#8b5cf6',
      },
      cards: [
        { icon: BookOpen, accent: '#ef4444', kicker: 'Version livre', title: 'Feuilleter le livret interactif', body: '7 chapitres à lire chapitre par chapitre. Progression sauvegardée sur ton téléphone.<br><a href="/livret-plateau" class="underline font-black text-red-400">Ouvrir le livre →</a>' },
        { icon: Sparkles, accent: '#f97316', kicker: 'Version PDF', title: 'Télécharger le livret complet', body: 'La version A4 imprimable premium, à conserver dans ton classeur.<br><a href="/ressources-pedagogiques" class="underline font-black text-orange-400">Voir les ressources →</a>' },
      ],
      cardsCols: 2,
    },
    {
      part: 'Le mot de la fin',
      title: 'Le plateau n\'est pas seulement un examen',
      paragraphs: [
        "Il est tentant de considérer le plateau comme une série d'exercices obligatoires à réussir pour obtenir son permis. Ce serait passer à côté de son véritable intérêt.",
        "Le plateau permet d'apprendre à connaître les <strong>réactions de la moto</strong> dans un environnement sécurisé. On y développe progressivement le regard, l'équilibre, la précision, le freinage, la maîtrise de l'allure et la capacité à réagir rapidement. <strong>Toutes ces compétences seront ensuite utilisées sur la route.</strong>",
        "Alors pendant tes premières heures, ne cherche pas immédiatement le chronomètre ou la perfection. Cherche d'abord à comprendre ce que fait ta moto. Puis <em>répète, corrige, recommence</em>. Et progressivement, quelque chose change : tu ne réfléchis plus à chaque commande. <strong>Tu commences réellement à piloter.</strong>",
      ],
      highlight: {
        kicker: '✨ CODE 2 ROUES',
        title: 'Apprendre. Comprendre. S\'entraîner. Progresser.',
        body: "Parce que le permis A2 n'est pas une ligne d'arrivée. <strong>C'est le début d'une grande liberté.</strong>",
        accent: '#ef4444',
      },
    },
  ],
  relatedRessources: [
    { label: 'Livret de progression Plateau A1/A2 — Version interactive', href: '/livret-plateau', desc: '7 chapitres cochables sur téléphone' },
    { label: 'Ressources pédagogiques (PDF téléchargeable)', href: '/ressources-pedagogiques', desc: 'Livret complet + infographies' },
    { label: '1,60 m et permis A2 : petite taille, grandes solutions', href: '/magazine/permis-a2-petit-gabarit-160-solutions', desc: 'Article pédagogique complémentaire' },
  ],
};
